import { FC, useEffect, useReducer, useMemo } from 'react';
import { css } from '@emotion/react';
import { spacing, colors } from '../../styles';
import { ToolOutputEvent } from '../../types/streaming';
import {
  TodoListRenderer,
  StockInfoRenderer,
  NewsRenderer,
  SearchResultsRenderer,
  WebSearchRenderer,
  FetchUrlRenderer,
  CollectionsRenderer,
  CatalystRenderer,
  ResearchRenderer,
  EarningsRenderer,
  PeersRenderer,
  RatingsRenderer,
  StatsRenderer,
  TechnicalRenderer,
  OptionsRenderer,
  InsiderRenderer,
  SentimentRenderer,
  MarketSentimentRenderer,
  TechnicalAnalysisRenderer,
  PortfolioRenderer,
  BacktestRenderer,
  AnalysisRenderer,
  CorrelationRenderer,
  ChartRenderer,
  TranscriptSearchRenderer,
  TradesRenderer,
  CollectionStocksRenderer,
  FilterStocksRenderer,
  GenericRenderer,
  MarketMapRenderer,
  PositionSizeRenderer,
  AiTradeIdeasRenderer,
} from './Tools';
import { OptionsHistoryRenderer } from './Tools/OptionsHistoryRenderer';

interface ToolOutputDisplayProps {
  toolOutput: ToolOutputEvent;
  showLatestBadge?: boolean;
}

// Auto-expand configuration - extract to constants for clarity and maintainability
const AUTO_EXPAND_TOOLS = ['todo_write', 'get_price_charts'] as const;
const AUTO_EXPAND_PATTERNS: readonly string[] = [];

// State management for expand/collapse behavior
type ExpandState = {
  expanded: boolean;
  userControlled: boolean;
  hasReceivedOutput: boolean;
};

type ExpandAction = { type: 'OUTPUT_RECEIVED'; toolName?: string } | { type: 'USER_TOGGLE' };

const getInitialExpanded = (toolName?: string): boolean => {
  if (!toolName) return false;

  // Check exact matches first (more performant)
  if (AUTO_EXPAND_TOOLS.includes(toolName as any)) return true;

  // Check patterns (more flexible but slower)
  return AUTO_EXPAND_PATTERNS.some((pattern) => toolName.includes(pattern));
};

// Move reducer outside component for performance
const expandReducer = (state: ExpandState, action: ExpandAction): ExpandState => {
  switch (action.type) {
    case 'OUTPUT_RECEIVED':
      // Prevent infinite loops and preserve user choice
      if (state.userControlled || state.hasReceivedOutput) return state;
      return {
        expanded: getInitialExpanded(action.toolName),
        userControlled: false,
        hasReceivedOutput: true,
      };
    case 'USER_TOGGLE':
      return {
        ...state, // preserve hasReceivedOutput
        expanded: !state.expanded,
        userControlled: true,
      };
  }
};

const ToolOutputDisplay: FC<ToolOutputDisplayProps> = ({ toolOutput, showLatestBadge = false }) => {
  // Initialize with correct default state to prevent flicker
  const [state, dispatch] = useReducer(expandReducer, {
    expanded: getInitialExpanded(toolOutput.tool_name),
    userControlled: false,
    hasReceivedOutput: false,
  });

  const parseToolOutput = (output: string) => {
    try {
      return JSON.parse(output);
    } catch {
      return { summary: output };
    }
  };

  const getToolDisplayName = (toolName?: string, isLoading = false): string => {
    if (!toolName) return isLoading ? 'Processing...' : 'Tool executed';

    const loadingNameMap: { [key: string]: string } = {
      todo_write: 'Updating todo list...',
      search_web: 'Searching the web...',
      get_symbols_metadata: 'Retrieving stock info...',
      get_news_for_symbols: 'Retrieving news...',
      fetch_url_content: 'Retrieving web content...',
      fetch_collections: 'Retrieving collections...',
      research_stock: 'Researching stock...',
      technical_analysis: 'Analyzing technicals...',
      earnings_analysis: 'Analyzing earnings...',
      get_peers: 'Analyzing peers...',
      get_financial_ratings: 'Retrieving financial ratings...',
      get_symbols_stats: 'Retrieving key stats...',
      embedded_expectations: 'Analyzing growth expectations...',
      growth_expectations: 'Analyzing growth expectations...',
      pricing_power: 'Analyzing pricing power...',
      analyze_earnings: 'Analyzing earnings...',
      option_flows: 'Analyzing option flows...',
      insider_activity: 'Analyzing insider activity...',
      market_sentiment: 'Analyzing market sentiment...',
      catalyst_generation: 'Generating catalysts...',
      generate_catalysts: 'Generating catalysts...',
      analyze_fundamentals: 'Analyzing fundamentals...',
      portfolio_generation: 'Generating portfolio...',
      portfolio_review: 'Reviewing portfolio...',
      backtest_portfolio_tool: 'Backtesting portfolio...',
      get_portfolio_weighted_returns: 'Analyzing portfolio returns...',
      correlation_matrix: 'Analyzing correlation...',
      drawdown_analysis: 'Analyzing drawdown...',
      stock_rating: 'Generating stock rating...',
      edge_detection: 'Analyzing edge...',
      risk_mitigation: 'Analyzing risks...',
      scenario_simulation: 'Simulating scenarios...',
      get_price_charts: 'Loading charts...',
      search_transcript: 'Searching transcripts...',
      analyze_trades: 'Analyzing trades...',
      collection_stocks: 'Loading collection stocks...',
      get_collection_stocks: 'Loading collection stocks...',
      filter_stocks: 'Filtering stocks...',
      analyze_technicals: 'Analyzing technicals...',
      analyze_options_history: 'Analyzing historical options...',
      get_collections: 'Retrieving collections...',
      get_market_sentiment: 'Analyzing market sentiment...',
      analyze_portfolio: 'Analyzing portfolio...',
      get_market_map: 'Loading market map...',
      get_financial_charts: 'Loading financial charts...',
      calculate_position_size: 'Calculating position size...',
      get_ai_trade_ideas: 'Fetching AI trade ideas...',
    };

    const completedNameMap: { [key: string]: string } = {
      todo_write: 'Updated todo list',
      search_web: 'Searched the web',
      get_symbols_metadata: 'Received stock info',
      get_news_for_symbols: 'Retrieved news',
      fetch_url_content: 'Retrieved web content',
      fetch_collections: 'Retrieved collections',
      research_stock: 'Researched stock',
      technical_analysis: 'Analyzed technicals',
      earnings_analysis: 'Analyzed earnings',
      get_peers: 'Analyzed peers',
      get_financial_ratings: 'Retrieved financial ratings',
      get_symbols_stats: 'Retrieved key stats',
      embedded_expectations: 'Analyzed growth expectations',
      growth_expectations: 'Analyzed growth expectations',
      pricing_power: 'Analyzed pricing power',
      analyze_earnings: 'Analyzed earnings',
      option_flows: 'Analyzed option flows',
      insider_activity: 'Analyzed insider activity',
      market_sentiment: 'Analyzed market sentiment',
      catalyst_generation: 'Generated catalysts',
      generate_catalysts: 'Generated catalysts',
      analyze_fundamentals: 'Analyzed fundamentals',
      portfolio_generation: 'Generated portfolio',
      portfolio_review: 'Reviewed portfolio',
      backtest_portfolio_tool: 'Backtested portfolio',
      get_portfolio_weighted_returns: 'Analyzed portfolio returns',
      correlation_matrix: 'Analyzed correlation',
      drawdown_analysis: 'Analyzed drawdown',
      stock_rating: 'Generated stock rating',
      edge_detection: 'Analyzed edge',
      risk_mitigation: 'Analyzed risks',
      scenario_simulation: 'Simulated scenarios',
      get_price_charts: 'Generated charts',
      search_transcript: 'Searched transcripts',
      analyze_trades: 'Analyzed trades',
      collection_stocks: 'Loaded collection stocks',
      get_collection_stocks: 'Loaded collection stocks',
      filter_stocks: 'Filtered stocks',
      analyze_technicals: 'Analyzed technicals',
      analyze_options_history: 'Analyzed historical options',
      get_collections: 'Retrieved collections',
      get_market_sentiment: 'Analyzed market sentiment',
      analyze_portfolio: 'Analyzed portfolio',
      get_market_map: 'Retrieved market map',
      get_financial_charts: 'Generated financial charts',
      calculate_position_size: 'Calculated position size',
      get_ai_trade_ideas: 'Got AI trade ideas',
    };

    const nameMap = isLoading ? loadingNameMap : completedNameMap;
    return (
      nameMap[toolName] ||
      (isLoading
        ? `${toolName.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}...`
        : toolName.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()))
    );
  };

  // Memoize parsed output to prevent unnecessary re-renders
  const parsed = useMemo(() => parseToolOutput(toolOutput.output), [toolOutput.output]);
  const isLoading = parsed.loading === true;

  // Helper function to sanitize and truncate search query
  const sanitizeSearchQuery = (query: string | undefined): string | undefined => {
    if (!query) return undefined;
    // Limit length to prevent UI overflow and potential XSS
    const MAX_QUERY_LENGTH = 100;
    const trimmed = query.trim().slice(0, MAX_QUERY_LENGTH);
    // Basic HTML entity escaping is handled by React's default text rendering
    return trimmed;
  };

  // Extract transcript search metadata for enhanced display
  const transcriptSearchMetadata = useMemo(() => {
    if (toolOutput.tool_name !== 'search_transcript') return null;

    const rawQuery = parsed.transcript_search_results?.query || parsed.search_query || parsed.query;
    const searchQuery = sanitizeSearchQuery(rawQuery);
    const symbol =
      parsed.transcript_search_results?.transcript_info?.symbol || parsed.transcript_info?.symbol || parsed.symbol;

    return searchQuery || symbol ? { searchQuery, symbol } : null;
  }, [toolOutput.tool_name, parsed]);

  // Memoize tool display name for performance (prevents recalculation on every render)
  const toolDisplayName = useMemo(() => {
    const baseName = getToolDisplayName(toolOutput.tool_name, isLoading);

    // Enhance transcript search display with query and symbol
    if (transcriptSearchMetadata && !isLoading) {
      const { searchQuery, symbol } = transcriptSearchMetadata;
      if (searchQuery && symbol) {
        return `${baseName} - ${symbol} "${searchQuery}"`;
      } else if (symbol) {
        return `${baseName} - ${symbol}`;
      } else if (searchQuery) {
        return `${baseName} - "${searchQuery}"`;
      }
    }

    return baseName;
  }, [toolOutput.tool_name, isLoading, transcriptSearchMetadata]);

  // Trigger OUTPUT_RECEIVED when tool output completes (with infinite loop protection)
  // Fixed edge case: Include toolOutput.output directly to catch rapid updates
  useEffect(() => {
    if (!isLoading && toolOutput.output && !state.hasReceivedOutput) {
      dispatch({ type: 'OUTPUT_RECEIVED', toolName: toolOutput.tool_name });
    }
  }, [isLoading, toolOutput.output, toolOutput.tool_name, state.hasReceivedOutput]);

  // Choose the appropriate renderer based on tool type
  const renderToolOutput = () => {
    // Remove loading content - title shows loading state, no content needed
    if (isLoading) {
      return null;
    }

    const toolName = toolOutput.tool_name || '';

    // Debug logging to understand renderer selection (can be removed in production)
    // console.log('ToolOutputDisplay:', { toolName, parsed, outputKeys: Object.keys(parsed), hasNewsData: !!parsed.news });

    if (toolName === 'todo_write') {
      return <TodoListRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName.includes('metadata') || toolName.includes('stock_info') || toolName === 'get_symbols_metadata') {
      return <StockInfoRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName.includes('news')) {
      return <NewsRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName.includes('search') && toolName.includes('stock')) {
      return <SearchResultsRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'search_web') {
      return <WebSearchRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'fetch_url_content') {
      return <FetchUrlRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'fetch_collections') {
      return <CollectionsRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'catalyst_generation' || toolName === 'generate_catalysts') {
      return <CatalystRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (
      toolName === 'research_stock' ||
      toolName === 'comprehensive_stock_analysis' ||
      toolName === 'analyze_fundamentals'
    ) {
      return <ResearchRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'earnings_analysis' || toolName === 'analyze_earnings') {
      return <EarningsRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'get_peers') {
      return <PeersRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'get_financial_ratings') {
      return <RatingsRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'get_symbols_stats') {
      return <StatsRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'technical_analysis') {
      return <TechnicalRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'option_flows') {
      return <OptionsRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'insider_activity') {
      return <InsiderRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'market_sentiment') {
      return <SentimentRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (
      toolName === 'portfolio_generation' ||
      toolName === 'portfolio_review' ||
      toolName === 'get_portfolio_weighted_returns' ||
      toolName === 'drawdown_analysis'
    ) {
      return <PortfolioRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'correlation_matrix') {
      return <CorrelationRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'backtest_portfolio_tool') {
      return <BacktestRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (
      toolName === 'edge_detection' ||
      toolName === 'risk_mitigation' ||
      toolName === 'scenario_simulation' ||
      toolName === 'embedded_expectations' ||
      toolName === 'growth_expectations' ||
      toolName === 'pricing_power' ||
      toolName === 'stock_rating'
    ) {
      return <AnalysisRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'get_price_charts') {
      return <ChartRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    // New tool renderers
    if (toolName === 'search_transcript') {
      return <TranscriptSearchRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'analyze_trades') {
      return <TradesRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'collection_stocks' || toolName === 'get_collection_stocks') {
      return <CollectionStocksRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'filter_stocks') {
      return <FilterStocksRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    // Fixed mappings for existing renderers with different tool names
    if (toolName === 'analyze_technicals') {
      return <TechnicalAnalysisRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'analyze_options_history') {
      return <OptionsHistoryRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'get_collections') {
      return <CollectionsRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'get_market_sentiment') {
      return <MarketSentimentRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'analyze_portfolio') {
      return <PortfolioRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'get_market_map') {
      return <MarketMapRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'get_financial_charts') {
      return <ChartRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'calculate_position_size') {
      return <PositionSizeRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    if (toolName === 'get_ai_trade_ideas') {
      return <AiTradeIdeasRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
    }

    return <GenericRenderer toolName={toolName} output={parsed} isLoading={isLoading} />;
  };

  return (
    <div
      css={css`
        margin-bottom: ${spacing.deci};
        border: 1px solid #e5e5e5;
        border-radius: 4px;
        overflow: hidden;
        text-align: left;
        background-color: ${colors.snow};

        html[data-theme='dark'] & {
          border-color: #333;
          background-color: rgba(255, 255, 255, 0.02);
        }
      `}
    >
      {/* Header with tool name */}
      <div
        css={css`
          padding: ${spacing.centi} ${spacing.deci};
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: ${isLoading ? 'default' : 'pointer'};

          &:hover {
            background: ${isLoading ? 'transparent' : '#f8f9fa'};
          }

          html[data-theme='dark'] & {
            &:hover {
              background: ${isLoading ? 'transparent' : '#2a2a2a'};
            }
          }
        `}
        onClick={() => !isLoading && dispatch({ type: 'USER_TOGGLE' })}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: ${spacing.centi};
          `}
        >
          <span
            css={css`
              font-size: 14px;
              font-weight: 500;
              color: #1a1a1a;

              html[data-theme='dark'] & {
                color: #e5e5e5;
              }
            `}
          >
            {toolDisplayName}
          </span>

          {showLatestBadge && toolOutput.tool_name === 'todo_write' && (
            <span
              css={css`
                font-size: 10px;
                font-weight: 500;
                color: #28b5bd;
                background: rgba(40, 181, 189, 0.1);
                padding: 2px 6px;
                border-radius: 10px;
                text-transform: uppercase;
                letter-spacing: 0.5px;

                html[data-theme='dark'] & {
                  color: #40b5bd;
                  background: rgba(40, 181, 189, 0.15);
                }
              `}
            >
              Latest
            </span>
          )}
        </div>

        {!isLoading && (
          <span
            css={css`
              color: #666;
              font-size: 12px;
              transform: ${state.expanded ? 'rotate(90deg)' : 'rotate(0deg)'};
              transition: transform 0.15s ease;
            `}
          >
            ▶
          </span>
        )}
      </div>

      {/* Main content - only visible when expanded and not loading */}
      {state.expanded && !isLoading && (
        <div
          css={css`
            padding: ${toolOutput.tool_name?.includes('metadata') ||
            toolOutput.tool_name?.includes('stock_info') ||
            toolOutput.tool_name === 'get_symbols_metadata'
              ? '0'
              : spacing.deci};
            border-top: 1px solid #f0f0f0;

            html[data-theme='dark'] & {
              border-top-color: #333;
            }
          `}
        >
          {renderToolOutput()}
        </div>
      )}
    </div>
  );
};

export default ToolOutputDisplay;
